import { useEffect, useState } from "react"
import HtmlParser from "react-html-parser"
import image, { PRODUCT_COVER } from "lacinetek-common/src/images"
import { metadata, SVOD_BLOCK_TEXT, SVOD_END_DATE, TEXT_BUTTON } from "lacinetek-common/src/metadata"

import { getFilm } from "lib/film-fetcher"
import { useLanguages } from "lib/languages"
import { useCountry } from "lib/country"
import { useTranslation } from "lib/i18n/translation"
import { useVideoPlayer } from "lib/video-player"
import { useModal } from "lib/modal"
import { useAuth } from "lib/auth"
import { useMainCategories } from "lib/tools/categories"
import { useAccesses } from "lib/accesses"
import fullname from "lib/tools/name"

import Image from "components/common/image"
import { FilmOverlay } from "components/modals/svod/film-overlay"
import { BlockOverlay } from "components/modals/svod/block-overlay"
import SkeletonLink from "components/common/skeleton-link"
import { BlockTitle } from "./block-title"
import { DisplaySliders } from "./slider"

export const SpotlightFilm = ({ block, className, overlayAuto }) => {
	if (!block.products.items.length > 0) {
		console.log(`block "${block.name}" has no products to display`)
		return null
	}
	const { current } = useLanguages()
	const { country } = useCountry()
	const { c } = useTranslation()
	const { seeFilm, seeCategoryTeaser } = useVideoPlayer()
	const { show } = useModal()
	const { showLoginSvod } = useAuth()
	const { hasSubscribed } = useAccesses()
	const { allLists } = useMainCategories()

	const [productLoading, setProductLoading] = useState(true)
	const [product, setProduct] = useState(true)
	const [video] = useState(block.videos.items[0])

	const endDate = metadata(block.products.items[0], SVOD_END_DATE)

	const handleWatchMovie = (event, product) => {
		event.preventDefault()
		if (hasSubscribed) {
			seeFilm(product)

			return
		}
		showLoginSvod()
	}

	const handleProductModal = (event, product) => {
		event.preventDefault()
		show({
			id: "modal-film",
			children: <FilmOverlay product={product} />,
			className: "modal film standalone stackable expanded",
			isPreview: true
		})
	}

	const handleWatchTeaser = (event) => {
		event.preventDefault()
		seeCategoryTeaser(block)
	}

	useEffect(() => {
		if (productLoading) {
			getFilm(block.products.items[0].id, current, country)
				.then(result => {
					setProduct(result)
					setProductLoading(false)
				})
				.catch(e => { console.log(e) })
		}
	}, [productLoading])

	useEffect(() => {
		getFilm(block.products.items[0].id, current, country)
			.then(result => {
				setProduct(result)
			})
			.catch(e => { console.log(e) })
	}, [current])

	useEffect(() => {
		if (overlayAuto) {
			show({
				id: "block-overlay",
				children: <BlockOverlay block={block} />,
				className: "is-archive modal stackable"
			})
		}
	}, [])

	if (productLoading) {
		return <div className="is-loading centered" ></div>
	}

	const handleSpotlightModal = (event, block) => {
		event.preventDefault()
		show({
			id: "block-overlay",
			children: <BlockOverlay block={block} />,
			className: "is-archive modal stackable"
		})
	}

	return (
		<section id={block.id} className={className}>
			<BlockTitle block={block} />
			{
				block.description !== ""
				&& HtmlParser(block.description)
			}
			<section className="focus svod">
				<div className="infos">
					{
						endDate
						&& <span className="alert short large icon-clock">
							{`${c("until")} ${endDate}`}
						</span>
					}
					<span className="title-alt large"><strong>{product.name}</strong></span>
					<div className="film-infos">
						<span className="director">{`${c("from")} ${product.director}`}</span>
						<span className="duration">{product.duration}</span>
						<span className="year">{product.year}</span>
						<span className="country">{product.origin}</span>
					</div>
				</div>
				<nav className="focus-nav" aria-label={c("header-navigation")}>
					<a
						href="page-offers-svod.php"
						rel="nofollow noopener"
						title={c("watch-the-movie")}
						className="icon-play alt"
						onClick={event => handleWatchMovie(event, product)}
					>
						{c("watch-the-movie")}
					</a>
					<a
						href="#"
						rel="nofollow noopener"
						title={c("see-single-film")}
						className="icon-file"
						onClick={event => handleProductModal(event, product)}
					>
						{c("see-single-film")}
					</a>
				</nav>
				<figure className="background">
					<picture>
						<Image
							src={image(product, PRODUCT_COVER)}
							alt={product.name}
						/>
					</picture>
				</figure>
			</section>
			<section className="short">
				<h2 className="hidden">{c("recommendations")}</h2>
				{
					product.recommendersIds.length > 0
					&& <p className="is-recommended"><b>{c("recommended")}</b>
						{
							product.recommendersIds.map((id, index) => {
								const list = allLists.find(l => l.director.id == id)

								if (!list) {
									return null
								}

								return (
									<SkeletonLink
										key={`director-link-${list.director.id}`}
										href={`/lists/[id]`}
										as={`/lists/${list.linkRewrite}`}
									>
										<a title={c("see-film-single")}>
											{list.name.replace(" | ", " ")}
										</a>
									</SkeletonLink>
								)
							})
						}
					</p>
				}
			</section>
			<section>
				<h2 className="hidden">{c("about-cineclub")}</h2>
				<div className="splitted">
					<aside>
						<div className="videoplayer youtube" style={{ backgroundImage: `url(${video.thumbnail})` }}>
							<div className="play" onClick={handleWatchTeaser}></div>
						</div>
					</aside>
					<div className="content">
						{HtmlParser(metadata(block, SVOD_BLOCK_TEXT))}
					</div>
				</div>
			</section>
			{
				metadata(block, TEXT_BUTTON)
				&& <p className="section-link centered">
					<a
						href="#"
						rel="nofollow noopener"
						title={c("see-all-articles")}
						className="button"
						onClick={event => handleSpotlightModal(event, block)}
					>
						{metadata(block, TEXT_BUTTON)}
					</a>
				</p>
			}
		</section>
	)
}
